import { useEffect, useState } from "react";
import { Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { parentApi } from "@/lib/parentApi";
import { useLanguage } from "@/context/LanguageContext";
import HistoryList from "@/components/parent-portal/HistoryList";

export default function ContactCoachesForm() {
  const { t } = useLanguage();
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState("general");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    try {
      const { data } = await parentApi.get("/parent/messages");
      setHistory(data || []);
    } catch {
      // Silent — history is non-critical
    }
  };
  useEffect(() => { loadHistory(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSending(true);
    try {
      await parentApi.post("/parent/contact", { subject, message, category });
      toast.success(t("parent.contactSent"));
      setSubject("");
      setMessage("");
      setCategory("general");
      loadHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const items = history.map((m) => ({
    id: m.id,
    primary: m.subject || "(no subject)",
    secondary: `${new Date(m.sent_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} · ${m.category}`,
    body: m.message,
  }));

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="bg-zinc-950 border-white/20 text-white">
            <SelectValue placeholder={t("parent.contactCategory")} />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/20 text-white">
            <SelectItem value="general">{t("parent.catGeneral")}</SelectItem>
            <SelectItem value="coaches">{t("parent.catCoaches")}</SelectItem>
            <SelectItem value="scheduling">{t("parent.catScheduling")}</SelectItem>
            <SelectItem value="billing">{t("parent.catBilling")}</SelectItem>
            <SelectItem value="other">{t("parent.catOther")}</SelectItem>
          </SelectContent>
        </Select>
        <div className="sm:col-span-2">
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={t("parent.contactSubject")}
            className="bg-zinc-950 border-white/20 text-white"
          />
        </div>
      </div>
      <div>
        <Label htmlFor="contact-message" className="sr-only">{t("parent.contactMessage")}</Label>
        <Textarea
          id="contact-message"
          required
          rows={4}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t("parent.contactMessagePlaceholder")}
          className="bg-zinc-950 border-white/20 text-white"
        />
      </div>
      <Button type="submit" disabled={sending || !message.trim()} className="bg-burns-red hover:bg-burns-red/80 text-white">
        {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
        {t("parent.contactSend")}
      </Button>
      <p className="text-zinc-500 text-xs">{t("parent.contactHint")}</p>

      <HistoryList items={items} title={t("parent.contactHistoryTitle")} />
    </form>
  );
}
